import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Badge, Button, Col, Input, Row, Table } from 'reactstrap';
import FormCard from '../../../shared/components/custom/FormCard';
import Api from '../../../util/api';
import { formatDateTime } from '../../../util/date-and-time';
import { partial } from '../../../util/translation/translation';

const p = partial('chat');

// Find chat messages by text or author, across the club's chats, deleted
// and hidden ones included; delete a message or block its author
const MessageSearchCard = ({ isBlocked, onBlockUser, onChanged }) => {
  const [q, setQ] = useState('');
  const [userId, setUserId] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim() && !userId.trim()) return;
    setSearching(true);
    try {
      setResults(
        await Api.chat.searchMessages({ q: q.trim(), userId: userId.trim() })
      );
    } catch (err) {
      toast.error(err || p('searchERROR'));
    } finally {
      setSearching(false);
    }
  };

  const remove = async (message) => {
    try {
      await Api.chat.deleteMessage(message.id);
      toast.success(p('messageDeleted'));
      setResults((current) =>
        current.map((item) =>
          item.id === message.id ? { ...item, deleted: true } : item
        )
      );
      onChanged?.();
    } catch {
      toast.error(p('errorDeletingMessage'));
    }
  };

  return (
    <FormCard title={p('searchMessages')} className="mb-3">
      <form onSubmit={search}>
        <Row>
          <Col md={6} className="mb-2">
            <Input
              placeholder={p('searchText')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Col>
          <Col md={4} className="mb-2">
            <Input
              placeholder={p('searchUserId')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
          </Col>
          <Col md={2} className="mb-2">
            <Button
              type="submit"
              color="primary"
              className="mb-0 w-100"
              disabled={searching}
            >
              {p('search')}
            </Button>
          </Col>
        </Row>
      </form>
      {results && (
        <Table responsive size="sm" className="mt-2 mb-0">
          <thead>
            <tr>
              <th>{p('logWhen')}</th>
              <th>{p('chat')}</th>
              <th>{p('user')}</th>
              <th>{p('message')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {results.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-muted">
                  {p('searchNoResults')}
                </td>
              </tr>
            ) : (
              results.map((message) => (
                <tr key={message.id}>
                  <td className="text-nowrap">
                    {formatDateTime(message.createdAt)}
                  </td>
                  <td>
                    <Link to={`/chats/edit/${message.chatId}`}>
                      {message.chatName || '-'}
                    </Link>
                  </td>
                  <td>
                    <Link to={`/users/edit/${message.userId}`}>
                      {message.employeeDetails?.userName ||
                        message.name ||
                        message.userId}
                    </Link>
                    {message.employeeDetails?.companyName && (
                      <div className="text-muted small">
                        {message.employeeDetails.companyName}
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 380, whiteSpace: 'pre-wrap' }}>
                    {message.text}
                    {message.deleted && (
                      <Badge color="secondary" className="ml-2">
                        {p('messageDeletedBadge')}
                      </Badge>
                    )}
                    {message.hidden && !message.deleted && (
                      <Badge color="warning" className="ml-2">
                        {p('messageHiddenBadge')}
                      </Badge>
                    )}
                  </td>
                  <td className="text-nowrap text-right">
                    {!message.deleted && (
                      <Button
                        size="sm"
                        color="link"
                        className="mb-0 p-0 mr-2 text-danger"
                        onClick={() => remove(message)}
                      >
                        {p('deleteMessage')}
                      </Button>
                    )}
                    {message.userId !== 'deleted-user' &&
                      (isBlocked(message.userId) ? (
                        <Badge color="secondary">{p('userIsBlocked')}</Badge>
                      ) : (
                        <Button
                          size="sm"
                          color="link"
                          className="mb-0 p-0"
                          onClick={() =>
                            onBlockUser(
                              message.userId,
                              message.employeeDetails?.userName || message.name
                            )
                          }
                        >
                          {p('blockUser')}
                        </Button>
                      ))}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
    </FormCard>
  );
};

export default MessageSearchCard;
